import { BillableElement, Board } from "../../../../reducers/uploadedBoard";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

export enum SortingOption {
  ALL = "All",
  POPULATE = "Populate",
  DO_NOT_POPULATE = "Do Not Populate",
  NEEDS_ATTENTION = "Needs Attention",
}

const SEPARATOR = `+`;

export const keyIsEmpty = (key: string): boolean => {
  return key.split("").every((c) => c == SEPARATOR);
};

const stringsToKey = (strings: string[]): string => {
  return strings.join(SEPARATOR);
};

export const elementToKey = (
  element: BillableElement,
  usePart: boolean,
): string => {
  // If the value is empty, make sure whatever we're passing to stringsToKey is empty. That way keyIsEmpty will evaluate to true
  return stringsToKey([
    element.value_text,
    element.footprint_group_key_component,
    element.do_not_populate ? "DNP" : "",
    ...(usePart
      ? [element.part_id || "", element.is_consignment ? "consigned" : ""]
      : []),
  ]);
};

export const elementsCouldBeUpdatedTogether = (
  element: BillableElement,
  otherElement: BillableElement,
) => {
  const key1 = elementToKey(element, false);
  const key2 = elementToKey(otherElement, false);
  return key1 === key2;
};

export function createGroupedElements(
  elements: BillableElement[],
): GroupedElements[] {
  return Object.values(
    elements.reduce(
      (groups, element) => {
        const elementKey = elementToKey(element, true);
        if (keyIsEmpty(elementKey)) {
          groups[uuidv4()] = { elements: [element] };
          return groups;
        }
        if (elementKey in groups) {
          groups[elementKey]!.elements.push(element);
        } else {
          groups[elementKey] = { elements: [element] };
        }
        return groups;
      },
      {} as { [key: string]: GroupedElements },
    ),
  );
}

export const BLANK = "-";

export type GroupedElements = {
  elements: BillableElement[];
};

export function useSortElements(board: Board) {
  const getElementAlert = useGetElementAlert(board);

  return useCallback(
    (
      elementsToSort: GroupedElements[],
      sortingOption: SortingOption,
    ): GroupedElements[] => {
      const elements = [...elementsToSort];
      if (sortingOption == SortingOption.NEEDS_ATTENTION) {
        elements.sort((a, b) => {
          if (
            getElementAlert(a.elements[0]!) !== null &&
            getElementAlert(b.elements[0]!) === null
          ) {
            return -1;
          } else {
            return 1;
          }
        });
      } else if (sortingOption == SortingOption.POPULATE) {
        elements.sort((a, b) => {
          if (
            a.elements[0]!.do_not_populate &&
            !b.elements[0]!.do_not_populate
          ) {
            return 1;
          } else if (
            !a.elements[0]!.do_not_populate &&
            b.elements[0]!.do_not_populate
          ) {
            return -1;
          }
          return 0;
        });
      } else if (sortingOption == SortingOption.DO_NOT_POPULATE) {
        elements.sort((a, b) => {
          if (
            a.elements[0]!.do_not_populate &&
            !b.elements[0]!.do_not_populate
          ) {
            return -1;
          } else if (
            !a.elements[0]!.do_not_populate &&
            b.elements[0]!.do_not_populate
          ) {
            return 1;
          }
          return 0;
        });
      }
      return elements;
    },
    [getElementAlert],
  );
}

export function useGetElementAlert(board: Board) {
  const partsWithInventoryIssues = board.quote.issues
    .filter((issue) => issue.part_id !== null)
    .map((issue) => issue.part_id!);
  return useCallback(
    (element: BillableElement): string | null => {
      if (element.do_not_populate) {
        return null;
      } else if (element.part_id === null) {
        return "No part is selected but the part is populated. Either uncheck the populate checkbox or select a part";
      } else if (element.is_consignment) {
        return null;
      } else if (partsWithInventoryIssues.includes(element.part_id)) {
        return "Selected part has insufficient inventory for board quantity";
      }
      return null;
    },
    [partsWithInventoryIssues],
  );
}
